import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Typography,
    Collapse,
    IconButton,
    Divider
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, ChevronLeft, LogOut } from "lucide-react";
import { usePermissions } from "../../context/PermissionContext";
import { isPathActive } from "../../utils.js/permissionUtils";
import SidebarProfile from "./SidebarProfile";

// ── Design tokens ──────────────────────────────────────────────
const BG = "#FFFFFF";
const GOLD = "#FFBD65";
const ACTIVE_BG = "rgba(255, 189, 101, 0.10)";
const BG_HOVER = "rgba(0, 0, 0, 0.04)";
const TEXT_PRI = "#111111";
const TEXT_SEC = "#222222";
const DIVIDER_C = "#F0F0F0";

// ── Reusable sx fragments ──────────────────────────────────────
const menuBtnSx = (collapsed, highlighted) => ({
    borderRadius: "8px",
    py: "6px",
    px: "8px",
    mb: "2px",
    justifyContent: collapsed ? "center" : "flex-start",
    bgcolor: highlighted ? ACTIVE_BG : "transparent",
    borderLeft: highlighted ? `2px solid ${GOLD}` : "2px solid transparent",
    boxShadow: "none",
});

const childBtnSx = (active) => ({
    pl: "36px",
    pr: "8px",
    py: "5px",
    mb: "1px",
    borderRadius: "8px",
    bgcolor: active ? ACTIVE_BG : "transparent",
    boxShadow: "none",
    "&:hover": { bgcolor: active ? ACTIVE_BG : BG_HOVER },
});

const labelSx = (highlighted, isChild) => ({
    fontSize: isChild ? "0.65rem" : "0.7rem",
    fontWeight: highlighted ? 600 : 400,
    color: highlighted ? GOLD : (isChild ? TEXT_SEC : TEXT_PRI),
    letterSpacing: "0.01em",
    ...(isChild ? {} : { lineHeight: 1.3 }),
});

// ── Child menu item ────────────────────────────────────────────
const ChildMenuItem = React.memo(({ child, active, onNavigate }) => (
    <ListItem disablePadding>
        <ListItemButton onClick={() => onNavigate(child)} sx={childBtnSx(active)}>
            <ListItemText
                primary={<Typography sx={labelSx(active, true)}>{child.text}</Typography>}
            />
        </ListItemButton>
    </ListItem>
));

// ── Parent / leaf menu item ────────────────────────────────────
const MenuItem = React.memo(({ item, collapsed, highlighted, isOpen, isActive, onNavigate }) => {
    const hasChildren = item.children?.length > 0;

    return (
        <>
            <ListItem disablePadding>
                <ListItemButton
                    disableRipple disableTouchRipple
                    onClick={() => onNavigate(item)}
                    title={collapsed ? item.text : undefined}
                    sx={menuBtnSx(collapsed, highlighted)}
                >
                    {item.icon && (
                        <ListItemIcon sx={{ minWidth: collapsed ? "auto" : 32 }}>
                            <item.icon
                                size={16}
                                color={highlighted ? GOLD : TEXT_SEC}
                                strokeWidth={highlighted ? 2.5 : 1.8}
                            />
                        </ListItemIcon>
                    )}

                    {!collapsed && (
                        <ListItemText
                            primary={<Typography sx={labelSx(highlighted, false)}>{item.text}</Typography>}
                        />
                    )}

                    {hasChildren && !collapsed && (
                        <Box sx={{ ml: "auto", color: TEXT_SEC, display: "flex", flexShrink: 0 }}>
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </Box>
                    )}
                </ListItemButton>
            </ListItem>

            {hasChildren && !collapsed && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {item.children.map((child) => (
                            <ChildMenuItem
                                key={child.text}
                                child={child}
                                active={isActive(child.path)}
                                onNavigate={onNavigate}
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
});

// ── Main SideBar ───────────────────────────────────────────────
const SideBar = ({ setSidebarWidth, onLogout, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [expandedMenu, setExpandedMenu] = useState(null);
    const [collapsed, setCollapsed] = useState(false);

    const { menuTree, loadingMenus, fetchMenus, pathMap } = usePermissions();

    // Load menus once
    useEffect(() => { fetchMenus(); }, [fetchMenus]);

    // Sync sidebar width
    useEffect(() => {
        if (setSidebarWidth) setSidebarWidth(collapsed ? 60 : 240);
    }, [collapsed, setSidebarWidth]);

    // Active-path helpers
    const isActive = useCallback(
        (path) => isPathActive(path, location.pathname),
        [location.pathname]
    );

    const isChildActive = useCallback(
        (children) => children?.some((c) => isPathActive(c.path, location.pathname)),
        [location.pathname]
    );

    // Compute once: does any parent have an active child?
    const hasAnyActiveChild = useMemo(() => {
        return menuTree?.some(
            (p) => p.children?.some((c) => isPathActive(c.path, location.pathname))
        ) ?? false;
    }, [menuTree, location.pathname]);

    // Auto-expand the parent whose child is active
    useEffect(() => {
        if (!menuTree?.length) return;
        const activeParent = menuTree.find(
            (item) => item.children?.some((c) => isPathActive(c.path, location.pathname))
        );
        setExpandedMenu(activeParent ? activeParent.text : null);
    }, [location.pathname, menuTree]);

    // Collapse sub-menus when sidebar is collapsed
    useEffect(() => {
        if (collapsed) setExpandedMenu(null);
    }, [collapsed]);

    // DRY navigate + close
    const navigateTo = useCallback(
        (path) => { navigate(path); if (onClose) onClose(); },
        [navigate, onClose]
    );

    const handleNavigation = useCallback((item) => {
        if (item.children?.length > 0) {
            if (collapsed) {
                setCollapsed(false);
                setExpandedMenu(item.text);
            } else {
                setExpandedMenu((prev) => (prev === item.text ? null : item.text));
            }
        } else if (item.path) {
            navigateTo(item.path);
        }
    }, [collapsed, navigateTo]);

    const handleLogoClick = useCallback(() => {
        if (pathMap?.["/dashboard"]) { navigateTo("/dashboard"); return; }

        const first = menuTree?.find((i) => i.path && !i.children?.length);
        if (first) { navigateTo(first.path); return; }

        const parent = menuTree?.find((i) => i.children?.length > 0);
        if (parent?.children[0]?.path) { navigateTo(parent.children[0].path); return; }

        navigateTo("/dashboard");
    }, [pathMap, menuTree, navigateTo]);

    return (
        <Box
            sx={{
                width: collapsed ? 60 : 240,
                bgcolor: BG,
                height: "100%",
                maxHeight: "100%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                borderRight: `1px solid ${DIVIDER_C}`,
                transition: "width 0.2s ease",
            }}
        >
            {/* ── Sticky Header ── */}
            <Box sx={{ flexShrink: 0, bgcolor: BG, position: "sticky", top: 0, zIndex: 10 }}>
                {/* Logo + collapse toggle */}
                <Box
                    sx={{
                        px: collapsed ? "10px" : 1.75,
                        pt: 1.5,
                        pb: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: collapsed ? "center" : "space-between",
                        minHeight: 48,
                    }}
                >
                    {!collapsed && (
                        <img
                            src="/Logo.webp"
                            alt="Heal at India"
                            title="Heal at India"
                            loading="lazy"
                            style={{
                                width: "118px",
                                height: "28px",
                                objectFit: "contain",
                                cursor: "pointer",
                                flexShrink: 0,
                            }}
                            onClick={handleLogoClick}
                        />
                    )}
                    <IconButton
                        onClick={() => setCollapsed((p) => !p)}
                        size="small"
                        sx={{
                            color: TEXT_SEC,
                            display: { xs: "none", md: "inline-flex" },
                            bgcolor: BG_HOVER,
                            borderRadius: "6px",
                            p: "3px",
                            flexShrink: 0,
                            boxShadow: "none",
                        }}
                    >
                        {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
                    </IconButton>
                </Box>

                {/* Profile */}
                <Box sx={{ px: collapsed ? "10px" : 1.75, pb: 1 }}>
                    <SidebarProfile collapsed={collapsed} onClose={onClose} />
                </Box>

                <Divider sx={{ borderColor: DIVIDER_C, mx: collapsed ? "10px" : 1.75, mb: 0.75 }} />
            </Box>

            {/* ── Scrollable menu ── */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    px: collapsed ? "6px" : "10px",
                    py: 0.5,
                    "&::-webkit-scrollbar": { width: "3px" },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.08)", borderRadius: "4px" },
                }}
            >
                <List sx={{ p: 0 }}>
                    {loadingMenus && !menuTree?.length && (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                            <CircularProgress size={18} sx={{ color: GOLD }} />
                        </Box>
                    )}

                    {menuTree?.map((item) => {
                        const hasChildren = item.children?.length > 0;
                        const childActive = hasChildren && isChildActive(item.children);
                        const active = !hasChildren && isActive(item.path);
                        const isOpen = expandedMenu === item.text;

                        // If a child route is active anywhere, only highlight its parent;
                        // otherwise highlight the currently expanded parent.
                        const highlighted = hasChildren
                            ? (hasAnyActiveChild ? childActive : isOpen)
                            : active;

                        return (
                            <MenuItem
                                key={item.text}
                                item={item}
                                collapsed={collapsed}
                                highlighted={highlighted}
                                isOpen={isOpen}
                                isActive={isActive}
                                onNavigate={handleNavigation}
                            />
                        );
                    })}
                </List>
            </Box>

            {/* ── Sticky Footer: Logout ── */}
            <Box
                sx={{
                    flexShrink: 0,
                    bgcolor: BG,
                    position: "sticky",
                    bottom: 0,
                    zIndex: 10,
                    mt: "auto",
                }}
            >
                <Divider sx={{ borderColor: DIVIDER_C, mx: collapsed ? "6px" : "10px", mb: 1 }} />
                <Box sx={{ px: collapsed ? "6px" : "10px", pb: 2 }}>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={onLogout}
                            title={collapsed ? "Logout" : undefined}
                            sx={{
                                borderRadius: "8px",
                                py: "6px",
                                px: "8px",
                                justifyContent: collapsed ? "center" : "flex-start",
                                boxShadow: "none",
                                "&:hover": { bgcolor: BG_HOVER },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: collapsed ? "auto" : 32 }}>
                                <LogOut size={16} color={TEXT_SEC} strokeWidth={1.8} />
                            </ListItemIcon>
                            {!collapsed && (
                                <ListItemText
                                    primary={
                                        <Typography sx={{ fontSize: "0.74rem", fontWeight: 400, color: TEXT_SEC }}>
                                            Logout
                                        </Typography>
                                    }
                                />
                            )}
                        </ListItemButton>
                    </ListItem>
                </Box>
            </Box>
        </Box>
    );
};

export default SideBar;
